/**
 * Mentor registration + FM pairing eligibility (mirrors Cloud Functions rules).
 */

export const FM_PAIRING_EXCLUDED = 'excluded';
export const FM_PAIRING_ELIGIBLE = 'eligible';

/** Legacy flyers before registrationMentorApproved existed. */
export function isLegacyMentorRegistrationApproved(flyer) {
  if (!flyer || flyer.type !== 'mentor') return false;
  if (flyer.registrationMentorApproved === true) return true;
  if (flyer.registrationMentorApproved === false) return false;
  const st = (flyer.status || '').toString().toLowerCase();
  return st.includes('accept') || st === 'accepted';
}

export function isRegistrationMentorApproved(flyer) {
  if (!flyer || flyer.type !== 'mentor') return false;
  if (flyer.registrationMentorManualOverride === true) return true;
  if (flyer.registrationMentorApproved === true) return true;
  if (flyer.registrationMentorApproved === false) return false;
  return isLegacyMentorRegistrationApproved(flyer);
}

export function isFmMentorPairingExcluded(flyer) {
  return !!(flyer && flyer.fmMentorPairingDecision === FM_PAIRING_EXCLUDED);
}

/**
 * True when this mentor flyer represents someone still on the event roster for this role
 * (enrollment / waitlist semantics from Fury sync), before staff approval for pairing.
 */
export function isMentorOnRosterForRole(flyer) {
  if (!flyer || flyer.type !== 'mentor' || flyer.test) return false;
  if (flyer.cancelled) return false;
  if (flyer.waiting) return false;
  return true;
}

/**
 * Staff denied / partial deny / stale pipeline `cancelled` while still on roster (`flyer.cancelled` false).
 * Not assignable in Team Pairing; shown separately from “pending staff approval”.
 */
export function isMentorRegistrationDeclinedForPairing(flyer) {
  if (!flyer || flyer.type !== 'mentor' || flyer.test) return false;
  const st = (flyer.registrationMentorStatus || '').toString().toLowerCase().trim();
  if (st === 'declined' || st === 'partial_denied') return true;
  if (st === 'cancelled' && !flyer.cancelled) return true;
  return false;
}

export function isMentorEligibleForPairing(flyer) {
  if (!flyer || flyer.type !== 'mentor' || flyer.test) return false;
  if (flyer.cancelled) return false;
  if (flyer.waiting) return false;
  if (isFmMentorPairingExcluded(flyer)) return false;
  if (isMentorRegistrationDeclinedForPairing(flyer)) return false;
  if (!isRegistrationMentorApproved(flyer)) return false;
  return true;
}

/** Shown crossed out / excluded section: declined by FM policy but still visible for audit. */
export function isMentorExcludedFromPairingOnly(flyer) {
  if (!flyer || flyer.type !== 'mentor' || flyer.test) return false;
  if (flyer.cancelled || flyer.waiting) return false;
  return isFmMentorPairingExcluded(flyer);
}

export function isMentorPendingStaffApproval(flyer) {
  if (!flyer || flyer.type !== 'mentor' || flyer.test) return false;
  if (flyer.cancelled || flyer.waiting) return false;
  if (isFmMentorPairingExcluded(flyer)) return false;
  if (isMentorRegistrationDeclinedForPairing(flyer)) return false;
  return !isRegistrationMentorApproved(flyer);
}

/** True when staff can call Fury to approve this mentor registration (paid/pending, has Fury id). */
export function mentorNeedsFuryApproveForAssignment(flyer) {
  if (!flyer || flyer.type !== 'mentor' || flyer.test) return false;
  if (flyer.cancelled || flyer.waiting || isFmMentorPairingExcluded(flyer)) return false;
  if (isMentorRegistrationDeclinedForPairing(flyer)) return false;
  if (isRegistrationMentorApproved(flyer)) return false;
  return !!(flyer.furyRegistrationId || '').toString().trim();
}

/**
 * Post-import / dashboard counts for mentor flyers.
 * @param {object[]} flyers
 */
export function computeMentorPipelineSummary(flyers) {
  const mentors = (flyers || []).filter((f) => f && f.type === 'mentor' && !f.test);
  let pendingStaff = 0;
  let approvedReg = 0;
  let fmExcluded = 0;
  let waitingList = 0;
  let cancelled = 0;
  let partialDenied = 0;
  for (const m of mentors) {
    if (m.cancelled) {
      cancelled++;
      continue;
    }
    if (m.waiting) {
      waitingList++;
      continue;
    }
    if (m.fmMentorPairingDecision === FM_PAIRING_EXCLUDED) {
      fmExcluded++;
      continue;
    }
    if (m.registrationMentorStatus === 'partial_denied') {
      partialDenied++;
      continue;
    }
    if (isRegistrationMentorApproved(m)) approvedReg++;
    else pendingStaff++;
  }
  return {
    pendingStaff,
    approvedReg,
    fmExcluded,
    waitingList,
    partialDenied,
    cancelled,
    total: mentors.length,
  };
}

export function registrationMentorStatusLabel(flyer) {
  if (!flyer || flyer.type !== 'mentor') return '';
  if (flyer.registrationMentorManualOverride) return 'Approved (manual)';
  const s = flyer.registrationMentorStatus;
  if (s === 'approved' || flyer.registrationMentorApproved === true) return 'Approved (reg)';
  if (s === 'pending_staff') return 'Pending staff';
  if (s === 'waiting_list') return 'Waiting list';
  if (s === 'cancelled') return flyer.cancelled === true ? 'Off roster' : 'Declined';
  if (s === 'declined') return 'Declined';
  if (s === 'partial_denied') return 'Partial denial';
  if (s === 'unknown') return 'Unknown';
  return (flyer.status || '').toString() || '—';
}
